'use strict';

const _ = require('lodash');
const Promise = require('bluebird');
const axios = require('axios');
const Connection = require('./Connection');

/**
 * This module is intended for making requests to the chat REST API
 */
class Api {
    /**
     * Set the basic parameters for this module to work
     * @param  {string} appId       App ID
     * @param  {string} server      Chat server URL
     * @param  {string} apiVersion  REST API version
     * @return {Api}                Api object for chaining
     */
    configure (appId, server, apiVersion) {
        this.appId = appId;
        this.server = server;
        this.apiVersion = apiVersion;

        return this;
    }

    /**
     * Makes a cross domain ajax request to the chat server
     * @param  {string} route     Chat API route
     * @param  {string} method    Request method
     * @param  {object} params    Request parameters object
     * @param  {object} options   Options to be added to the request
     * @return {Promise}
     */
    request (route, method, params = {}, options = {}) {
        // Normalize method
        method = method.toLowerCase();
        // Check for allowed methods
        const validMethods = ['get', 'post', 'put', 'delete'];
        if (validMethods.indexOf(method) === -1) {
            return Promise.reject(new Error('Method not valid'));
        }

        const requestData = this._buildRequestData(route, method, params);
        if (!_.isEmpty(options)) {
            // If there's any options, add them to the request object
            Object.keys(options).forEach((prop) => {
                requestData[prop] = options[prop];
            });
        }

        return axios.request(requestData)
            .catch((err) => {
                if (err.message !== 'Network Error') {
                    return Promise.reject(err);
                }
            });
    }

    /**
     * Getter for the REST API version
     * @return {String} API version
     */
    getApiVersion () {
        return this.apiVersion;
    }

    /**
     * Get the full route path
     * @param  {string} route Route to call
     * @return {string}       Full route URL
     */
    getFullPath (route) {
        return [this.server, this.apiVersion].join('/') + route;
    }

    _buildRequestData (route, method, params) {
        // Build the common request data
        let requestData = {
            method: method,
            url: this.getFullPath(route),
            headers: {
                'x-hyper-appid': this.appId,
                Pragma: 'no-cache'
            },
        };

        // If a token needs to be added, do it via headers
        if (Connection.getToken()) {
            requestData.headers['x-hyper-token'] = Connection.getToken();
        }
        if (!_.isEmpty(params)) {
            this._moveAuthToHeaders(requestData, params);
        }

        // Depending on the method, add data to wherever it corresponds
        if (method === 'post' || method === 'put') {
            // Put the data in the req body
            requestData.data = params;
        } else {
            // Put the data in the query string
            requestData.params = params;
        }

        return requestData;
    }

    _moveAuthToHeaders (requestData, params) {
        const authParameters = ['appId', 'token', 'secret', 'key'];
        authParameters.forEach((param) => {
            if (params[param]) {
                const headerName = 'x-hyper-' + param.toLowerCase();
                requestData.headers[headerName] = params[param];
                delete params[param];
            }
        });
        // if there were already headers in params, delete them
        delete params.headers;
        return requestData;
    }
}

module.exports = new Api();



// WEBPACK FOOTER //
// ./src/Api.js